/**
 * Dynamic packages: flight deals must match user-selected departure / return dates (hotel stay intent).
 * Rejects LDS RoundTrip "packages" whose leg dates don't match the search calendar.
 */

import { computeNightsFromSearchPhrase } from "../hotelSearchListDisplay";
import { firstLegFromLdsDeal } from "./mergeSelfComposeDeals";

/** @param {string|null|undefined} s */
export function datePartFromLdsDateTime(s) {
  if (!s || typeof s !== "string") return null;
  const part = s.trim().split(/\s+/)[0];
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(part)) return null;
  return part;
}

/** @param {string|null|undefined} s */
export function normalizeUserDdMmYyyy(s) {
  if (!s || typeof s !== "string") return null;
  const t = s.trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(t)) return t;
  return datePartFromLdsDateTime(t);
}

/**
 * @param {object|null|undefined} flightNode — LDS flight node with .leg
 * @returns {object|null}
 */
export function firstLegFromLdsFlightNode(flightNode) {
  if (!flightNode?.leg) return null;
  const L = flightNode.leg;
  return Array.isArray(L) ? L[0] : L;
}

/**
 * @param {object|null|undefined} deal — LDS deal with linkedFlights.flight
 * @returns {{ outboundLeg: object|null, inboundLeg: object|null }}
 */
export function roundTripDealDirectionLegs(deal) {
  const lf = deal?.linkedFlights;
  if (!lf) return { outboundLeg: null, inboundLeg: null };
  const fRaw = lf.flight;
  const flights =
    fRaw == null ? [] : Array.isArray(fRaw) ? fRaw : [fRaw];
  if (flights.length < 1) return { outboundLeg: null, inboundLeg: null };
  const outboundLeg = firstLegFromLdsFlightNode(flights[0]);
  const inboundLeg =
    flights.length > 1 ? firstLegFromLdsFlightNode(flights[1]) : null;
  return { outboundLeg, inboundLeg };
}

/**
 * @param {object|null|undefined} deal
 * @param {string|null|undefined} expectedOutboundDdMmYyyy
 * @param {string|null|undefined} expectedInboundDdMmYyyy
 */
export function roundTripDealMatchesSearchDates(
  deal,
  expectedOutboundDdMmYyyy,
  expectedInboundDdMmYyyy
) {
  const expOut = normalizeUserDdMmYyyy(expectedOutboundDdMmYyyy);
  const expIn = normalizeUserDdMmYyyy(expectedInboundDdMmYyyy);
  if (!expOut || !expIn) return false;
  const { outboundLeg, inboundLeg } = roundTripDealDirectionLegs(deal);
  if (!outboundLeg || !inboundLeg) return false;
  const outDep = datePartFromLdsDateTime(outboundLeg.depDate);
  const inDep = datePartFromLdsDateTime(inboundLeg.depDate);
  if (!outDep || !inDep) return false;
  return outDep === expOut && inDep === expIn;
}

/**
 * OneWay LDS deal: single direction; first leg dep must match the searched dep_date.
 * @param {object|null|undefined} deal
 * @param {string|null|undefined} expectedDdMmYyyy
 */
export function oneWayDealFirstDepMatches(deal, expectedDdMmYyyy) {
  const exp = normalizeUserDdMmYyyy(expectedDdMmYyyy);
  if (!exp) return false;
  const leg = firstLegFromLdsDeal(deal);
  if (!leg) return false;
  const dep = datePartFromLdsDateTime(leg.depDate);
  return !!dep && dep === exp;
}

/**
 * Calendar nights between first departure and return departure (flight-centric; not necessarily hotel nights).
 * @param {object|null} outboundLeg
 * @param {object|null} inboundLeg
 */
export function nightsBetweenFlightDepartures(outboundLeg, inboundLeg) {
  const a = datePartFromLdsDateTime(outboundLeg?.depDate);
  const b = datePartFromLdsDateTime(inboundLeg?.depDate);
  if (!a || !b) return null;
  try {
    const [da, ma, ya] = a.split("/").map(Number);
    const [db, mb, yb] = b.split("/").map(Number);
    const d0 = new Date(ya, ma - 1, da);
    const d1 = new Date(yb, mb - 1, db);
    const n = Math.round((d1 - d0) / (1000 * 60 * 60 * 24));
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch {
    return null;
  }
}

/**
 * @param {object[]} rtDealsSorted
 * @param {object[]} outDealsSorted
 * @param {object[]} inDealsSorted
 * @param {object} ctx
 * @param {string|null|undefined} ctx.expectedOutboundDate — flight dep (calendar)
 * @param {string|null|undefined} ctx.expectedInboundDate — flight return dep (calendar) = hotel checkout day
 * @param {string|null|undefined} ctx.hotelCheckIn
 * @param {string|null|undefined} ctx.hotelCheckOut
 */
export function resolveDynamicPackageParallelFlightPick({
  rtDealsSorted = [],
  outDealsSorted = [],
  inDealsSorted = [],
  expectedOutboundDate,
  expectedInboundDate,
  hotelCheckIn,
  hotelCheckOut,
}) {
  const selected_nights_from_request = computeNightsFromSearchPhrase({
    checkIn: hotelCheckIn,
    checkOut: hotelCheckOut,
  });

  const expOut = normalizeUserDdMmYyyy(expectedOutboundDate);
  const expIn = normalizeUserDdMmYyyy(expectedInboundDate);
  const computed_checkout_date = normalizeUserDdMmYyyy(hotelCheckOut) || expIn;

  const filteredRt = Array.isArray(rtDealsSorted)
    ? rtDealsSorted.filter((d) =>
        roundTripDealMatchesSearchDates(d, expOut, expIn)
      )
    : [];
  const filteredOut = Array.isArray(outDealsSorted)
    ? outDealsSorted.filter((d) => oneWayDealFirstDepMatches(d, expOut))
    : [];
  const filteredIn = Array.isArray(inDealsSorted)
    ? inDealsSorted.filter((d) => oneWayDealFirstDepMatches(d, expIn))
    : [];

  let dealForAvailable = null;
  let flightResultsBrowseMode = "roundTrip";
  let reject_reason_if_mismatch = null;
  let outbound_departure_date = null;
  let inbound_departure_date = null;
  let final_package_nights = null;

  const rawRtCount = rtDealsSorted?.length ?? 0;
  if (rawRtCount > 0 && filteredRt.length === 0) {
    reject_reason_if_mismatch =
      "round_trip_deals_rejected_dates_not_matching_search";
  }

  if (filteredRt.length > 0) {
    /* כל דילי RT שעברו סינון תאריכים — לא רק [0]; אחרת availableFlights נשאר אובייקט בודד ומסך «טיסות מוכנות» מציג אופציה אחת */
    dealForAvailable =
      filteredRt.length === 1 ? filteredRt[0] : filteredRt;
    flightResultsBrowseMode = "roundTrip";
    const primaryRt =
      Array.isArray(dealForAvailable) ? dealForAvailable[0] : dealForAvailable;
    const { outboundLeg, inboundLeg } = roundTripDealDirectionLegs(primaryRt);
    outbound_departure_date =
      datePartFromLdsDateTime(outboundLeg?.depDate) || expOut;
    inbound_departure_date =
      datePartFromLdsDateTime(inboundLeg?.depDate) || expIn;
    final_package_nights = nightsBetweenFlightDepartures(
      outboundLeg,
      inboundLeg
    );
  } else if (filteredOut.length > 0 && filteredIn.length > 0) {
    flightResultsBrowseMode = "selfCompose";
    dealForAvailable = filteredOut[0];
    const o0 = firstLegFromLdsDeal(filteredOut[0]);
    const i0 = firstLegFromLdsDeal(filteredIn[0]);
    outbound_departure_date = datePartFromLdsDateTime(o0?.depDate) || expOut;
    inbound_departure_date = datePartFromLdsDateTime(i0?.depDate) || expIn;
    final_package_nights = nightsBetweenFlightDepartures(o0, i0);
    if (!reject_reason_if_mismatch) {
      reject_reason_if_mismatch =
        rawRtCount > 0
          ? "using_self_compose_after_rt_mismatch"
          : null;
    }
  } else if (filteredOut.length > 0) {
    flightResultsBrowseMode = "selfCompose";
    dealForAvailable = filteredOut[0];
    const o0 = firstLegFromLdsDeal(filteredOut[0]);
    outbound_departure_date = datePartFromLdsDateTime(o0?.depDate) || expOut;
    reject_reason_if_mismatch =
      reject_reason_if_mismatch || "missing_matching_inbound_one_way";
  } else if (filteredIn.length > 0) {
    flightResultsBrowseMode = "selfCompose";
    dealForAvailable = filteredIn[0];
    const i0 = firstLegFromLdsDeal(filteredIn[0]);
    inbound_departure_date = datePartFromLdsDateTime(i0?.depDate) || expIn;
    reject_reason_if_mismatch =
      reject_reason_if_mismatch || "missing_matching_outbound_one_way";
  } else {
    dealForAvailable = null;
    reject_reason_if_mismatch =
      reject_reason_if_mismatch || "no_flights_match_selected_dates";
  }

  const hotel_stay_matches_intent =
    selected_nights_from_request != null &&
    final_package_nights != null &&
    selected_nights_from_request === final_package_nights;

  const debug = {
    selected_nights_from_request,
    computed_checkout_date,
    outbound_departure_date,
    inbound_departure_date,
    hotel_checkin: normalizeUserDdMmYyyy(hotelCheckIn),
    hotel_checkout: computed_checkout_date,
    final_package_nights,
    hotel_stay_matches_intent,
    reject_reason_if_mismatch,
    counts: {
      rt_raw: rawRtCount,
      rt_filtered: filteredRt.length,
      out_filtered: filteredOut.length,
      in_filtered: filteredIn.length,
    },
  };

  const autoSelfCompose =
    filteredRt.length === 0 &&
    filteredOut.length > 0 &&
    filteredIn.length > 0;

  return {
    filteredRtDeals: filteredRt,
    filteredOutDeals: filteredOut,
    filteredInDeals: filteredIn,
    dealForAvailable,
    flightResultsBrowseMode,
    autoSelfCompose,
    autoOutboundDeal: autoSelfCompose ? filteredOut[0] : null,
    autoInboundDeal: autoSelfCompose ? filteredIn[0] : null,
    debug,
  };
}

/**
 * @param {object[]} owDeals
 * @param {string|null|undefined} expectedOutboundDate
 */
export function filterDynamicPackageOneWayDeals(owDeals, expectedOutboundDate) {
  if (!Array.isArray(owDeals)) return [];
  return owDeals.filter((d) =>
    oneWayDealFirstDepMatches(d, expectedOutboundDate)
  );
}
